import type { AttributeStateResponse, SourceTypeCode } from '../../api/types';
import { AttributeRow } from './AttributeRow';
import styles from './AttributesSection.module.css';

/**
 * The Attributes section (docs/UX_ARCHITECTURE.md §3.3): the whole
 * attribute library for this person, grouped by the framework's own
 * categories, with unassessed attributes present but de-emphasised.
 *
 * The summary line at the top states how much of the library has actually
 * been assessed. That sentence is the honest frame for everything below it:
 * without it, a profile showing eight ratings looks like a complete picture
 * of a person rather than eight facts and fifteen unknowns.
 *
 * Within each category, assessed attributes are listed ahead of unassessed
 * ones (Visual QA Round 2) — real information ahead of the gaps in the
 * record, rather than interleaved in whatever order the library happens to
 * list them. Nothing is hidden or resorted by value: every attribute is
 * still one `AttributeRow`, still expandable to its own full detail
 * (`AttributeRow`'s own compact-summary note explains why an unassessed
 * row's collapsed line carries less on it — not less behind it).
 */
export function AttributesSection({
  attributes,
  employeeId,
  employeeName,
  viewerCanAssessAs,
  onAssessmentRecorded,
}: {
  attributes: AttributeStateResponse[];
  employeeId: string;
  employeeName: string;
  /** Threaded from the profile response, where the API derived it. */
  viewerCanAssessAs: SourceTypeCode | null;
  onAssessmentRecorded: () => void;
}) {
  const assessedCount = attributes.filter((a) => a.composite !== null).length;
  const disagreementCount = attributes.filter((a) => a.confidence.highVariance).length;

  const summary =
    `${assessedCount} of ${attributes.length} assessed` +
    (disagreementCount > 0 ? ` · ${disagreementCount} with disagreement between sources` : '');

  const categories = new Map<string, AttributeStateResponse[]>();
  for (const state of attributes) {
    const bucket = categories.get(state.attribute.categoryLabel);
    if (bucket) bucket.push(state);
    else categories.set(state.attribute.categoryLabel, [state]);
  }

  return (
    <section className={styles.section} aria-labelledby="attributes-heading">
      <header className={styles.header}>
        <h2 id="attributes-heading" className={styles.heading}>
          Attributes
        </h2>
        <p className={styles.summary}>{summary}</p>
      </header>

      <p className={styles.guidance}>
        Ratings are on a 1–20 scale where 10–11 is the expected level for a capable employee. A rating and the
        confidence in it are separate facts: select any attribute to see the individual source ratings, their
        weights and the evidence behind them, and to open the recorded points its trajectory was classified from.
      </p>

      {[...categories.entries()].map(([categoryLabel, states]) => {
        // Assessed first, then unassessed — real information ahead of the
        // gaps in the record, rather than interleaved in library order. Each
        // group keeps the library's own order among itself; nothing is
        // resorted by value.
        const assessedStates = states.filter((s) => s.composite !== null);
        const unassessedStates = states.filter((s) => s.composite === null);

        return (
          <div key={categoryLabel} className={styles.category}>
            <h3 className={styles.categoryHeading}>
              {categoryLabel}
              <span className={styles.categoryCount}>
                {assessedStates.length}/{states.length}
              </span>
            </h3>
            {assessedStates.length > 0 ? (
              <ul className={styles.list}>
                {assessedStates.map((state) => (
                  <AttributeRow
                    key={state.attribute.id}
                    state={state}
                    employeeId={employeeId}
                    employeeName={employeeName}
                    viewerCanAssessAs={viewerCanAssessAs}
                    onAssessmentRecorded={onAssessmentRecorded}
                  />
                ))}
              </ul>
            ) : null}

            {unassessedStates.length > 0 ? (
              <>
                {assessedStates.length > 0 ? (
                  <p className={styles.unassessedLabel}>
                    Not yet assessed ({unassessedStates.length})
                  </p>
                ) : null}
                <ul className={styles.list}>
                  {unassessedStates.map((state) => (
                    <AttributeRow
                      key={state.attribute.id}
                      state={state}
                      employeeId={employeeId}
                      employeeName={employeeName}
                      viewerCanAssessAs={viewerCanAssessAs}
                      onAssessmentRecorded={onAssessmentRecorded}
                    />
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
